import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Format large numbers for Y-axis display
const formatYAxisValue = (value) => {
  // Handle invalid values
  if (value === null || value === undefined || !isFinite(value)) return '0';
  if (value === 0) return '0';

  // Handle very large numbers (likely corrupted data)
  if (Math.abs(value) >= 1e15) return '0';

  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + 'K';
  }
  return Math.round(value).toLocaleString();
};

const ChartJSLine = ({
  data,
  height = '300px',
  color = '#0D7463',
  showPoints = false,
  showArea = true,
  title = '',
  responsive = true,
  isCurrency = false,
  valuePrefix = '',
  valueSuffix = ''
}) => {
  // Handle empty or invalid data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ height, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '14px' }}>
        No data available
      </div>
    );
  }

  // Sanitize data values to prevent corrupted/infinite values
  const sanitizeValue = (val) => {
    if (val === null || val === undefined || !isFinite(val) || Math.abs(val) >= 1e15) {
      return 0;
    }
    return val;
  };

  // Transform data for Chart.js
  const chartData = {
    labels: data.map(item => item.label),
    datasets: [
      {
        label: title,
        data: data.map(item => sanitizeValue(item.value)),
        borderColor: color,
        backgroundColor: showArea ? `${color}20` : 'transparent',
        borderWidth: 3,
        fill: showArea,
        tension: 0.4, // Smooth curves
        pointRadius: showPoints ? 4 : 0,
        pointHoverRadius: showPoints ? 6 : 0,
        pointBackgroundColor: color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        pointStyle: 'circle',
        spanGaps: false,
        segment: {
          borderColor: (ctx) => {
            // Dynamic color based on trend (with null checks)
            if (!ctx.p1?.parsed || !ctx.p0?.parsed) return color;
            const current = ctx.p1.parsed.y;
            const previous = ctx.p0.parsed.y;
            if (!isFinite(current) || !isFinite(previous)) return color;
            if (current > previous) return '#28C76F'; // Green for upward trend
            if (current < previous) return '#EA5455'; // Red for downward trend
            return color; // Default color for no change
          },
        },
      },
    ],
  };

  const options = {
    responsive: responsive,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll handle legend separately
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: color,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            const value = context.parsed.y;
            const formattedValue = isCurrency
              ? `₦${value.toLocaleString()}`
              : `${valuePrefix}${value.toLocaleString()}${valueSuffix}`;
            return `${title}: ${formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: '#F3F3F3',
          drawBorder: false,
        },
        ticks: {
          color: '#6B6B6B',
          font: {
            size: 11,
            weight: '500',
          },
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          display: true,
          color: '#F3F3F3',
          drawBorder: false,
        },
        ticks: {
          color: '#6B6B6B',
          font: {
            size: 11,
            weight: '500',
          },
          callback: function(value) {
            return formatYAxisValue(value);
          },
          maxTicksLimit: 6,
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.4, // Smooth curves
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <div style={{ height, width: '100%' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default ChartJSLine;
